import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import dashboardApiService from '../../services/dashboard/DashboardApiService';
import sshApiService from '../../services/ssh/SshApiService';

const SshActivity = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [sshAlerts, setSshAlerts] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [error, setError] = useState(null);

  const fetchAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      const response = await dashboardApiService.getAllAgents();
      setAgents(response || []);
    } catch (err) {
      setError('Failed to fetch agents.');
      console.error('Fetch agents error:', err);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const fetchSshAlerts = useCallback(async (agentId) => {
    if (!agentId) return;
    try {
      setLoadingAlerts(true);
      setError(null);
      const response = await sshApiService.getSshAlerts(agentId);
      setSshAlerts(response.alerts || []);
    } catch (err) {
      setError(`Failed to fetch SSH alerts for agent ${agentId}.`);
      console.error('SSH alerts fetch error:', err);
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleAgentChange = (event) => {
    const agentId = event.target.value;
    setSelectedAgent(agentId);
  };

  const handleFetchClick = () => {
    fetchSshAlerts(selectedAgent);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        SSH Activity
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl fullWidth>
              <InputLabel id="agent-select-label">Select Agent</InputLabel>
              <Select
                labelId="agent-select-label"
                value={selectedAgent}
                label="Select Agent"
                onChange={handleAgentChange}
                disabled={loadingAgents}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name} (ID: {agent.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleFetchClick}
              disabled={!selectedAgent || loadingAlerts}
              startIcon={<SearchIcon />}
            >
              Fetch
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SSH Login Attempts
          </Typography>
          {loadingAlerts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Log Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sshAlerts.length > 0 ? (
                    sshAlerts.map((alert, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{formatTimestamp(alert['@timestamp'])}</TableCell>
                        <TableCell>{alert.rule.description}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{alert.full_log}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        {selectedAgent ? 'No SSH alerts found for this agent.' : 'Please select an agent to view alerts.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SshActivity;
